import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Tooltips for every control that names itself but shows no text.
 *
 * Icon-only buttons across the app (view switcher, filter, row menus, inline
 * edit, preview toolbar…) already carry an `aria-label` or a `title`, but an
 * aria-label is invisible to a sighted user and a native title waits a second
 * and ignores the theme. Wrapping each one in <Tooltip> by hand is how some
 * were missed; this listens once at the document and gives all of them the
 * same instant, themed bubble <Tooltip> draws.
 *
 * What gets a bubble:
 *  - anything with a `title` (the native one is parked while ours shows, so
 *    the two never stack);
 *  - a button, link, tab, switch or menu item with an `aria-label` and no
 *    visible text of its own — a labelled button that also reads "Save" needs
 *    no tooltip repeating it.
 * Controls already inside a <Tooltip> are left to it (data-tooltip-host), and
 * `title=""` opts an element — and its ancestors' titles — out.
 */

const NAMED = "[title],[data-parked-title],[aria-label]";
const INTERACTIVE =
  "button,a,summary,[role=button],[role=tab],[role=switch],[role=menuitem],[role=checkbox]";

type Tip = { x: number; y: number; below: boolean; text: string };

const labelFor = (el: HTMLElement): string | undefined => {
  const title = el.getAttribute("title") || el.dataset.parkedTitle;
  if (title?.trim()) return title.trim();

  const aria = el.getAttribute("aria-label")?.trim();
  if (!aria || !el.matches(INTERACTIVE)) return undefined;
  // Visible text already says what it is.
  if (el.innerText?.trim()) return undefined;
  return aria;
};

const findTarget = (from: EventTarget | null) => {
  let el = from instanceof Element ? from.closest<HTMLElement>(NAMED) : null;
  while (el) {
    if (el.closest("[data-tooltip-host]")) return null;
    // An empty title is a deliberate "no tooltip here": it also silences
    // whatever an ancestor would have said.
    if (el.getAttribute("title") === "" && el.dataset.parkedTitle == null)
      return null;
    const text = labelFor(el);
    if (text) return { el, text };
    el = el.parentElement?.closest<HTMLElement>(NAMED) ?? null;
  }
  return null;
};

export function GlobalTooltips() {
  const [tip, setTip] = useState<Tip | null>(null);
  const current = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const hide = () => {
      const el = current.current;
      if (el?.dataset.parkedTitle != null) {
        // Hand the native title back, unless the app changed it meanwhile.
        if (!el.getAttribute("title"))
          el.setAttribute("title", el.dataset.parkedTitle);
        delete el.dataset.parkedTitle;
      }
      current.current = null;
      setTip(null);
    };

    const show = (e: Event) => {
      const found = findTarget(e.target);
      if (!found) {
        if (current.current) hide();
        return;
      }
      const { el, text } = found;
      if (el === current.current) return;
      hide();

      if (el.getAttribute("title")) {
        // Blanked rather than removed: an empty title also keeps a titled
        // ancestor's native tooltip from stepping in while ours shows.
        el.dataset.parkedTitle = el.getAttribute("title") ?? "";
        el.setAttribute("title", "");
      }
      current.current = el;

      const r = el.getBoundingClientRect();
      const below = r.top < 44;
      // A wide trigger (a whole editable row) gets the bubble over the
      // pointer; centred, it would sit far from where the user is looking.
      const centre =
        r.width > 320 && e instanceof MouseEvent
          ? e.clientX
          : r.left + r.width / 2;
      setTip({
        x: Math.min(Math.max(centre, 8), window.innerWidth - 8),
        y: below ? r.bottom + 8 : r.top - 8,
        below,
        text,
      });
    };

    const leave = (e: MouseEvent) => {
      const el = current.current;
      if (!el) return;
      const to = e.relatedTarget;
      if (to instanceof Node && el.contains(to)) return;
      hide();
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") hide();
    };

    document.addEventListener("mouseover", show);
    document.addEventListener("focusin", show);
    document.addEventListener("mouseout", leave);
    document.addEventListener("focusout", hide);
    // The bubble is placed from a rect measured once; anything that moves or
    // replaces the trigger retires it.
    document.addEventListener("mousedown", hide, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      hide();
      document.removeEventListener("mouseover", show);
      document.removeEventListener("focusin", show);
      document.removeEventListener("mouseout", leave);
      document.removeEventListener("focusout", hide);
      document.removeEventListener("mousedown", hide, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, []);

  if (!tip) return null;

  return createPortal(
    <span
      role="tooltip"
      style={{
        position: "fixed",
        left: tip.x,
        top: tip.y,
        transform: tip.below ? "translate(-50%, 0)" : "translate(-50%, -100%)",
        maxWidth: "min(24rem, calc(100vw - 1rem))",
      }}
      className="
        pointer-events-none z-[100001]
        whitespace-pre-line rounded-md
        bg-[rgb(var(--tooltip-bg,var(--card)))]
        px-2 py-1
        text-xs font-medium
        text-[rgb(var(--tooltip-fg,var(--text-primary)))]
        shadow-lg
      "
    >
      {tip.text}
    </span>,
    document.body,
  );
}
